<template>
  <v-card outlined>
    <v-card-title>

      <v-row justify="space-between" align="center" no-gutters>
        <span>{{ receiptTitle }}</span>

        <v-chip label :color="allDelivered ? 'green lighten-4' : 'orange lighten-4'">
          <v-icon
            small
            :color="allDelivered ? 'green darken-2' : 'orange darken-2'"
            class="mr-2"
          >
            {{ allDelivered ? "mdi-check-circle" : "mdi-alert" }}
          </v-icon>
          <span
            :class="
              allDelivered ? 'green--text text--darken-2' : 'orange--text text--darken-2'
            "
          >
            {{ statusText }}
          </span>
        </v-chip>
      </v-row>
    </v-card-title>

    <v-divider />

    <v-card-text>
      <v-row>
        <v-col cols="12" md="6">
          <div class="receipt-overview-row">
            <div class="receipt-label">{{ $t("warehouse.purchase_order") }}:</div>
            <div class="receipt-value">#{{ editedItem.number || "-" }}</div>
          </div>
          <div class="receipt-overview-row">
            <div class="receipt-label">{{ $t("warehouse.ordering_date") }}:</div>
            <div class="receipt-value">{{ localeDate(editedItem.document_date) }}</div>
          </div>
          <div class="receipt-overview-row receipt-overview-row--warehouse">
            <div class="receipt-label">{{ $t("routes.magacin") }}:</div>
            <autocomplete
              v-model="editedItem.warehouse_id"
              :items="warehouses"
              item-text="name"
              item-value="id"
              hide-details
              color="white"
              :loading="loadingWarehouses"
            />
          </div>

          <div class="receipt-divider"></div>

          <div class="receipt-note-block">
            <div class="receipt-label">{{ $t("purchase_order_note") }}:</div>
            <div class="receipt-note-value">{{ orderNote || "-" }}</div>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div class="receipt-overview-row">
            <div class="receipt-label">{{ $t("supplier") }}:</div>
            <div class="receipt-value">{{ supplierName || "-" }}</div>
          </div>
          <div class="receipt-overview-row receipt-overview-row--field">
            <div class="receipt-label">{{ $t("warehouse.supplier_dispatch_note") }}:</div>
            <text-field
              :value="dispatcherNumber"
              hide-details
              color="white"
              dense
              @input="$emit('update:dispatcherNumber', $event)"
            />
          </div>
          <div class="receipt-overview-row receipt-overview-row--field">
            <div class="receipt-label">{{ $t("warehouse.delivery_date") }}:</div>
            <date-picker
              :value="deliveryDate"
              single-date
              hide-details
              hide-label
              color="white"
              @input="$emit('update:deliveryDate', $event)"
            />
          </div>
          <div class="receipt-overview-row receipt-overview-row--field">
            <div class="receipt-label">{{ $t("warehouse.received_by") }}:</div>
            <autocomplete
              v-model="editedItem.received_by"
              :items="users"
              hide-details
              return-object
              item-text="fullName"
              item-value="id"
              required
              :rules="[rules.required]"
              color="white"
              dense
            />
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import hasValidation from "@/mixins/hasValidation";
import hasWarehouseUsers from "@/mixins/warehouse/hasWarehouseUsers";
import hasReceiptCalculations from "@/mixins/warehouse/hasReceiptCalculations";

export default {
  name: "WarehouseReceiptInfoCard",
  mixins: [hasValidation, hasWarehouseUsers, hasReceiptCalculations],
  props: {
    editedItem: { type: Object, required: true },
    warehouses: { type: Array, default: () => [] },
    loadingWarehouses: { type: Boolean, default: false },
    groceries: { type: Array, default: () => [] },
    documentTransactions: { type: Array, default: () => [] },
    dispatcherNumber: { type: String, default: "" },
    deliveryDate: { default: null },
  },
  computed: {
    receiptTitle() {
      return `${this.$t("receipt_title")} #${this.editedItem?.id || ""}`.trim();
    },
    supplierName() {
      return this.editedItem.supplier?.name || this.editedItem.supplier_id?.name || "";
    },
    orderNote() {
      return this.editedItem.notes || "";
    },
    allDelivered() {
      return (
        this.groceries.length > 0 && this.groceries.every((g) => this.isFullyDelivered(g))
      );
    },
    statusText() {
      return this.allDelivered
        ? this.$t("status_delivered").toUpperCase()
        : this.$t("status_in_progress").toUpperCase();
    },
  },
};
</script>

<style scoped>
.receipt-overview-row {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: 40px;
}

.receipt-overview-row + .receipt-overview-row {
  margin-top: 6px;
}

.receipt-divider {
  margin: 14px 0 16px;
  height: 1px;
  background: #ede8e1;
}

.receipt-label {
  font-size: 0.92rem;
  color: #75685d;
}

.receipt-value {
  font-size: 0.96rem;
  font-weight: 700;
  color: #2b241e;
}

.receipt-note-block {
  display: grid;
  gap: 6px;
}

.receipt-note-value {
  color: #4a3f36;
  line-height: 1.45;
  white-space: pre-wrap;
}

@media (max-width: 960px) {
  .receipt-overview-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
}
</style>
